import { useState, useCallback, useEffect } from 'react';
import { notify } from '@/lib/notify';
import type { SessionStatus, AuthStartResult, AuthVerifyResult, MtprotoAuthStatus, QrCodeData } from '../types';
import { resetSessionExpiredFlag } from './useMediaUrl';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: string[];
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Извлекает сообщение об ошибке из ответа API
 */
function getErrorMessage(result: ApiResponse<unknown>): string {
  if (result.errors && result.errors.length > 0) {
    return result.errors[0];
  }
  return result.error || 'Неизвестная ошибка';
}

export function useTelegramAuth() {
  const [session, setSession] = useState<SessionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/session`);

      const result: ApiResponse<SessionStatus> = await response.json();
      if (result.success && result.data) {
        setSession(result.data);
        // Сбрасываем флаг истечения сессии при успешной авторизации
        if (result.data?.status === 'AUTHORIZED') {
          resetSessionExpiredFlag();
        }
      } else {
        // Если сессии нет или ошибка - сбрасываем состояние
        setSession(null);
      }
    } catch {
      // При ошибке сети сбрасываем сессию
      setSession(null);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const startAuth = useCallback(async (phoneNumber: string): Promise<AuthStartResult | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      // Проверяем статус ответа
      if (!response.ok) {
        // Пытаемся получить ошибку из тела ответа
        try {
          const result: ApiResponse<AuthStartResult> = await response.json();
          notify.error(getErrorMessage(result));
        } catch {
          notify.error(`Ошибка сервера: ${response.status}`);
        }
        return null;
      }

      const result: ApiResponse<AuthStartResult> = await response.json();

      if (!result.success || !result.data) {
        notify.error(getErrorMessage(result));
        return null;
      }

      notify.success('Код отправлен в Telegram');
      await fetchSession();
      return result.data;
    } catch (error) {
      if (error instanceof TypeError) {
        notify.error('Ошибка сети. Проверьте подключение.');
      } else {
        notify.error('Ошибка при отправке кода');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession]);

  const verifyCode = useCallback(async (code: string): Promise<AuthVerifyResult | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      // Проверяем статус ответа
      if (!response.ok) {
        // Пытаемся получить ошибку из тела ответа
        try {
          const result: ApiResponse<AuthVerifyResult> = await response.json();
          notify.error(getErrorMessage(result));
        } catch {
          notify.error(`Ошибка сервера: ${response.status}`);
        }
        return null;
      }

      const result: ApiResponse<AuthVerifyResult> = await response.json();

      if (!result.success || !result.data) {
        notify.error(getErrorMessage(result));
        return null;
      }

      if (result.data?.status === 'WAITING_PASSWORD') {
        notify.info('Требуется пароль 2FA');
      } else if (result.data?.status === 'AUTHORIZED') {
        notify.success('Авторизация успешна');
      }

      await fetchSession();
      return result.data;
    } catch (error) {
      if (error instanceof TypeError) {
        notify.error('Ошибка сети. Проверьте подключение.');
      } else {
        notify.error('Ошибка при проверке кода');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession]);

  const resendCode = useCallback(async (): Promise<AuthStartResult | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/resend-code`, {
        method: 'POST',
      });

      if (!response.ok) {
        try {
          const result: ApiResponse<AuthStartResult> = await response.json();
          notify.error(getErrorMessage(result));
        } catch {
          notify.error(`Ошибка сервера: ${response.status}`);
        }
        return null;
      }

      const result: ApiResponse<AuthStartResult> = await response.json();

      if (!result.success || !result.data) {
        notify.error(getErrorMessage(result));
        return null;
      }

      notify.success('Код отправлен повторно (SMS)');
      await fetchSession();
      return result.data;
    } catch (error) {
      if (error instanceof TypeError) {
        notify.error('Ошибка сети. Проверьте подключение.');
      } else {
        notify.error('Ошибка при повторной отправке кода');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession]);

  const verify2FA = useCallback(async (password: string): Promise<AuthVerifyResult | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      // Проверяем статус ответа
      if (!response.ok) {
        // Пытаемся получить ошибку из тела ответа
        try {
          const result: ApiResponse<AuthVerifyResult> = await response.json();
          notify.error(getErrorMessage(result));
        } catch {
          notify.error(`Ошибка сервера: ${response.status}`);
        }
        return null;
      }

      const result: ApiResponse<AuthVerifyResult> = await response.json();

      if (!result.success || !result.data) {
        notify.error(getErrorMessage(result));
        return null;
      }

      if (result.data?.status === 'AUTHORIZED') {
        notify.success('Авторизация успешна');
      }

      await fetchSession();
      return result.data;
    } catch (error) {
      if (error instanceof TypeError) {
        notify.error('Ошибка сети. Проверьте подключение.');
      } else {
        notify.error('Ошибка при проверке пароля');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession]);

  const logout = useCallback(async (options?: { silent?: boolean }): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/logout`, {
        method: 'POST',
      });

      const result: ApiResponse<null> = await response.json();

      // Всегда сбрасываем локальную сессию при вызове logout
      // Даже если бэкенд вернул ошибку - пользователь хочет начать заново
      setSession(null);

      if (!result.success) {
        if (!options?.silent) {
          notify.error(result.error || 'Ошибка при выходе');
        }
        return false;
      }

      if (!options?.silent) {
        notify.success('Сессия завершена');
      }
      return true;
    } catch {
      // Даже при сетевой ошибке сбрасываем локальную сессию
      setSession(null);
      if (!options?.silent) {
        notify.error('Ошибка сети');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ==========================================
  // QR AUTH (как в Telegram Web A)
  // ==========================================

  const startQrAuth = useCallback(async (): Promise<QrCodeData | null> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/qr/start`, {
        method: 'POST',
      });

      const result: ApiResponse<QrCodeData> = await response.json();

      if (!result.success || !result.data) {
        notify.error(result.error || 'Ошибка при генерации QR кода');
        return null;
      }

      await fetchSession();
      return result.data;
    } catch {
      notify.error('Ошибка сети');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession]);

  const checkQrAuth = useCallback(async (): Promise<AuthVerifyResult | null> => {
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/auth/qr/check`);

      const result: ApiResponse<AuthVerifyResult> = await response.json();

      if (!result.success || !result.data) {
        return null;
      }

      if (result.data?.status === 'WAITING_PASSWORD') {
        notify.info('Требуется пароль 2FA');
        await fetchSession();
      } else if (result.data?.status === 'AUTHORIZED') {
        notify.success('Авторизация через QR успешна');
        await fetchSession();
      }

      return result.data;
    } catch {
      return null;
    }
  }, [fetchSession]);

  const cancelQrAuth = useCallback(async (): Promise<void> => {
    try {
      await fetch(`${API_URL}/api/telegram-client/auth/qr/cancel`, {
        method: 'POST',
      });
      await fetchSession();
    } catch {
      // ignore
    }
  }, [fetchSession]);

  // ==========================================
  // MULTI-ACCOUNT SUPPORT
  // ==========================================

  const [accounts, setAccounts] = useState<SessionStatus[]>([]);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts`);

      const result: ApiResponse<SessionStatus[]> = await response.json();
      if (result.success && result.data) {
        setAccounts(result.data);
      }
    } catch {
      // ignore
    }
  }, []);

  // Fetch accounts when session changes
  useEffect(() => {
    if (session?.status === 'AUTHORIZED') {
      fetchAccounts();
    }
  }, [session?.status, fetchAccounts]);

  const switchAccount = useCallback(async (sessionId: number): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionId}/activate`, {
        method: 'POST',
      });

      const result: ApiResponse<SessionStatus> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при переключении аккаунта');
        return false;
      }

      notify.success('Аккаунт переключен');
      await fetchSession();
      await fetchAccounts();
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession, fetchAccounts]);

  const removeAccount = useCallback(async (sessionId: number): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionId}`, {
        method: 'DELETE',
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при удалении аккаунта');
        return false;
      }

      notify.success('Аккаунт удален');
      await fetchSession();
      await fetchAccounts();
      return true;
    } catch {
      notify.error('Ошибка сети');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSession, fetchAccounts]);

  return {
    session,
    accounts,
    isLoading,
    isAuthorized: session?.status === 'AUTHORIZED',
    authStatus: session?.status as MtprotoAuthStatus | undefined,
    startAuth,
    verifyCode,
    resendCode,
    verify2FA,
    logout,
    startQrAuth,
    checkQrAuth,
    cancelQrAuth,
    refetchSession: fetchSession,
    refetchAccounts: fetchAccounts,
    switchAccount,
    removeAccount,
  };
}
